/**
 * Application_Form — decision-note routing pure logic
 * (application-decision-feedback spec, task 2.1).
 *
 * This module is the single source of truth for mapping a director's review
 * decision plus an incoming note to exactly one persisted note field. It is
 * **genuinely pure**: it performs no I/O, reads no globals, and never calls
 * `new Date()` or the AWS SDK — exactly like `phaseDeadlines.ts`. Because it
 * imports nothing backend-only, the identical routing is consumed by both the
 * Application_Form_Service (authoritative persistence in `reviewApplicationResponse`)
 * and the frontend mock client, so server and client can never disagree on how a
 * decision note is stored.
 *
 * Two persisted note fields exist on an ApplicationResponse:
 *   - **Return_Feedback_Note** (`returnFeedbackNote`): contestant-visible feedback
 *     authored on a REQUEST_CHANGES decision.
 *   - **Internal_Reject_Note** (`internalRejectNote`): director-only note authored
 *     on a REJECT decision, never exposed on any contestant-facing read path.
 *
 * Routing is a total function of `(decision, note)`. The caller applies the
 * result by replacing the note fields on the persisted item wholesale, so the
 * field NOT targeted by the decision is always cleared and a stale note from a
 * prior decision never survives a new decision.
 *
 * Validates: Requirements 1.1, 2.3, 3.5, 4.1, 4.2, 4.3, 4.4
 */

// ---------------------------------------------------------------------------
// Types

/**
 * A director review decision applied by the Decision_Operation
 * (`reviewApplicationResponse`).
 */
export type ReviewDecision = 'APPROVE' | 'REJECT' | 'REQUEST_CHANGES' | 'REOPEN';

/**
 * The routed note fields to apply to the persisted ApplicationResponse. At most
 * one field is present; a field is present only when a non-blank note accompanied
 * a decision that targets it. An absent field means "leave unset" (R4.4) — the
 * caller replaces the note fields wholesale, so an absent field is cleared.
 */
export interface DecisionNoteFields {
  /** Contestant-visible feedback set on REQUEST_CHANGES (Req 2.3, 4.2). */
  returnFeedbackNote?: string;
  /** Director-only note set on REJECT (Req 3.5, 4.3). */
  internalRejectNote?: string;
}

// ---------------------------------------------------------------------------
// Routing (pure, total)

/**
 * Route an incoming decision note to exactly one persisted field (Req 4).
 *
 * The note is trimmed first; a blank, whitespace-only, or absent (`undefined`/
 * `null`) note yields no field (Req 4.4), so the caller leaves the corresponding
 * attribute unset. A non-blank trimmed note is placed in:
 *   - `returnFeedbackNote` when the decision is `REQUEST_CHANGES` (Req 2.3, 4.2);
 *   - `internalRejectNote` when the decision is `REJECT` (Req 3.5, 4.3);
 *   - neither field when the decision is `APPROVE` or `REOPEN` (Req 1.1).
 *
 * The field not targeted by the decision is never populated; the caller clears
 * it by replacing the note fields wholesale. Because the returned object holds at
 * most one field and only ever the one matching the decision, applying it
 * guarantees that at most one note field is set on the persisted item and it is
 * always the one that matches the decision.
 *
 * Pure and total.
 */
export function routeDecisionNote(
  decision: ReviewDecision,
  note?: string | null,
): DecisionNoteFields {
  const trimmed = typeof note === 'string' ? note.trim() : '';
  if (trimmed.length === 0) return {};

  switch (decision) {
    case 'REQUEST_CHANGES':
      return { returnFeedbackNote: trimmed };
    case 'REJECT':
      return { internalRejectNote: trimmed };
    case 'APPROVE':
    case 'REOPEN':
      return {};
    default:
      return {};
  }
}

// ---------------------------------------------------------------------------
// Read-model boundary (pure, total)

/**
 * Owner/contestant read model (Req 5.1, 5.2, 5.3).
 *
 * Removes the director-only `internalRejectNote` field and returns everything
 * else unchanged — including the contestant-visible `returnFeedbackNote`. This
 * is the op-layer half of the two-independent-guarantees isolation of the
 * internal note: even though the stored `ApplicationResponseItem` carries the
 * internal note, no contestant-facing read path ever serializes it because this
 * function strips the key before the item leaves the owner op.
 *
 * The function is **total and pure**: it deletes a key that may or may not be
 * present, reads no globals, performs no I/O, and never throws regardless of the
 * stored item's shape or decision history — so it is safe for ANY item, not just
 * decided ones. The input is not mutated; a shallow copy is returned.
 */
export function toOwnerReadModel<T extends { internalRejectNote?: string }>(
  item: T,
): Omit<T, 'internalRejectNote'> {
  const { internalRejectNote: _internalRejectNote, ...rest } = item;
  return rest;
}

/**
 * Director read model (Req 5.4).
 *
 * Retains BOTH notes — the contestant-visible `returnFeedbackNote` and the
 * director-only `internalRejectNote` — so a requester holding director
 * authorization sees the full recorded context. Pure and total: it returns the
 * item unchanged (a shallow copy), reads no globals, performs no I/O, and never
 * throws for any item shape. Provided as the explicit counterpart to
 * `toOwnerReadModel` so the read-model boundary is expressed symmetrically at
 * the call sites.
 */
export function toDirectorReadModel<T>(item: T): T {
  return { ...item };
}
